/**
 * Tab de configuración de cuentas. Las cuentas dejaron de venir por seed
 * (decisión 2026-05-10) — se configuran aquí. Cada cuenta tiene un type
 * que cambia los campos requeridos: credit_card abre creditLimitCents.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Plus,
  Pencil,
  Archive,
  Trash2,
  Building2,
  CreditCard,
  Wallet,
  Box,
  Receipt,
  ListOrdered,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import clsx from "clsx";
import {
  accountCreateSchema,
  ACCOUNT_TYPES,
  PAYMENT_METHODS,
  PAYMENT_METHODS_BY_ACCOUNT_TYPE,
  type AccountCreateInput,
  type AccountType,
  type PaymentMethod,
} from "@axorial/shared";
import { api } from "@/lib/api";
import { formatMxnCents } from "@/lib/format";
import { useMonthlyBalances } from "@/features/finance/monthlyBalances";

interface Account {
  id: string;
  name: string;
  type: AccountType;
  bankName: string | null;
  creditLimitCents: number | null;
  cutDay: number | null;
  currency: string;
  openingBalanceCents: number;
  active: boolean;
  notes: string | null;
  allowedPaymentMethods: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod | null;
  loanOriginalCents: number | null;
  loanMonthlyPaymentCents: number | null;
  loanInterestRateBps: number | null;
  loanTotalPayments: number | null;
  loanPaymentDay: number | null;
  loanFolio: string | null;
  loanNextPaymentDate: string | null;
}

interface AccountBalance {
  accountId: string;
  type: AccountType;
  inflowCents: number;
  outflowCents: number;
  // bank/cash/other
  currentBalanceCents?: number;
  // credit_card
  creditLimitCents?: number | null;
  usedCents?: number;
  availableCents?: number;
  utilizationPct?: number | null;
  // loan
  loanOriginalCents?: number;
  loanMonthlyPaymentCents?: number | null;
  loanInterestRateBps?: number | null;
  loanTotalPayments?: number | null;
  loanPaymentDay?: number | null;
  loanFolio?: string | null;
  loanNextPaymentDate?: string | null;
  loanRemainingCents?: number;
  loanPaidCents?: number;
  loanProgressPct?: number | null;
}

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  transfer: "Transferencia",
  card_credit: "Tarjeta de crédito",
  card_debit: "Tarjeta de débito",
  cash: "Efectivo",
};

const TYPE_META: Record<AccountType, { label: string; icon: typeof Building2; color: string }> = {
  bank: { label: "Cuenta bancaria", icon: Building2, color: "text-info" },
  credit_card: { label: "Tarjeta de crédito", icon: CreditCard, color: "text-warning" },
  cash: { label: "Efectivo", icon: Wallet, color: "text-success" },
  loan: { label: "Préstamo / Crédito simple", icon: Receipt, color: "text-error" },
  other: { label: "Otra", icon: Box, color: "text-base-content/55" },
};

export function AccountsTab() {
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery<{ accounts: Account[] }>({
    queryKey: ["finance", "accounts", showAll],
    queryFn: () => api.get(`/finance/accounts${showAll ? "?all=1" : ""}`),
  });

  const balancesQuery = useQuery<{ balances: AccountBalance[] }>({
    queryKey: ["finance", "balances"],
    queryFn: () => api.get("/finance/balances"),
  });

  const accounts = data?.accounts ?? [];
  const balanceById = new Map((balancesQuery.data?.balances ?? []).map((b) => [b.accountId, b]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="cursor-pointer label gap-2 text-sm">
          <input
            type="checkbox"
            className="toggle toggle-sm toggle-primary"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          Mostrar archivadas
        </label>
        <button type="button" onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
          <Plus className="size-4" /> Nueva cuenta
        </button>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner text-primary" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="card-elevated p-8 text-center">
          <p className="text-sm text-base-content/65">
            Sin cuentas configuradas. Crea la primera con el botón arriba.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {accounts.map((a) => (
            <AccountCard
              key={a.id}
              account={a}
              balance={balanceById.get(a.id)}
              onEdit={() => setEditing(a)}
            />
          ))}
        </div>
      )}

      {creating && <AccountModal mode="create" onClose={() => setCreating(false)} />}
      {editing && <AccountModal mode="edit" account={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function AccountCard({
  account,
  balance,
  onEdit,
}: {
  account: Account;
  balance: AccountBalance | undefined;
  onEdit: () => void;
}) {
  const meta = TYPE_META[account.type];
  const Icon = meta.icon;
  const qc = useQueryClient();
  const navigate = useNavigate();

  const remove = useMutation({
    mutationFn: () => api.del(`/finance/accounts/${account.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance"] }),
  });

  return (
    <div className={clsx("card-elevated p-4 space-y-3", !account.active && "opacity-55")}>
      <div className="flex items-start gap-3">
        <div className={clsx("size-9 rounded-lg bg-base-200 grid place-items-center", meta.color)}>
          <Icon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate flex items-center gap-2">
            {account.name}
            {!account.active && <span className="badge badge-xs badge-ghost">Archivada</span>}
          </div>
          <div className="text-xs text-base-content/55 mt-0.5">
            {meta.label}
            {account.bankName && ` · ${account.bankName}`}
          </div>
        </div>
      </div>

      {account.type === "credit_card" ? (
        <CreditCardBalance account={account} balance={balance} />
      ) : account.type === "loan" ? (
        <LoanBalance account={account} balance={balance} />
      ) : (
        <BankBalance account={account} balance={balance} />
      )}

      {account.notes && (
        <div className="text-xs text-base-content/60 leading-relaxed border-t border-base-300/60 pt-2">
          {account.notes}
        </div>
      )}

      <div className="flex items-center gap-1 pt-1 border-t border-base-300/40">
        <button
          type="button"
          onClick={() =>
            navigate({
              to: "/",
              search: (prev) => ({
                ...prev,
                tab: "transacciones",
                account: account.id,
              }),
            })
          }
          className="btn btn-ghost btn-xs gap-1 text-info"
          title="Ver todos los movimientos de esta cuenta"
        >
          <ListOrdered className="size-3" /> Movimientos
        </button>
        <button type="button" onClick={onEdit} className="btn btn-ghost btn-xs gap-1">
          <Pencil className="size-3" /> Editar
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm(`¿Borrar / archivar "${account.name}"?`)) remove.mutate();
          }}
          disabled={remove.isPending}
          className="btn btn-ghost btn-xs gap-1 text-error"
        >
          {account.active ? (
            <>
              <Archive className="size-3" /> Archivar
            </>
          ) : (
            <>
              <Trash2 className="size-3" /> Borrar
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function BankBalance({
  account,
  balance,
}: {
  account: Account;
  balance: AccountBalance | undefined;
}) {
  const current = balance?.currentBalanceCents ?? account.openingBalanceCents;
  const negative = current < 0;
  return (
    <div className="space-y-1.5">
      <div>
        <div className="text-xs uppercase tracking-wider text-base-content/55 font-semibold">
          Saldo actual
        </div>
        <div
          className={clsx(
            "text-2xl font-display font-bold tabular-nums",
            negative ? "text-error" : "text-base-content",
          )}
        >
          {formatMxnCents(current)}
        </div>
      </div>
      {balance && (balance.inflowCents > 0 || balance.outflowCents > 0) && (
        <div className="text-xs text-base-content/55 flex items-center gap-3 font-mono tabular-nums">
          <span className="text-success">+{formatMxnCents(balance.inflowCents)}</span>
          <span className="text-error">−{formatMxnCents(balance.outflowCents)}</span>
        </div>
      )}
      {account.openingBalanceCents !== 0 && (
        <div className="text-xs text-base-content/45">
          Saldo inicial: {formatMxnCents(account.openingBalanceCents)}
        </div>
      )}
      <MonthlyHistory account={account} />
    </div>
  );
}

/**
 * Historial de la cuenta: saldo inicial / final + flujo de cada periodo,
 * computado en vivo por fin-api desde transacciones + transferencias
 * (endpoint /accounts/:id/monthly-balances). No depende de snapshots
 * consolidados, así que siempre está al día e incluye el periodo en curso.
 *
 * Para TDC con día de corte configurado agrupa por estado de cuenta
 * (periodo = fecha de corte) en vez de mes calendario, y el saldo corre
 * en convención "a favor positivo / deuda negativa".
 */
function MonthlyHistory({ account }: { account: Account }) {
  const [open, setOpen] = useState(false);
  const isCredit = account.type === "credit_card";
  const byCut = isCredit && account.cutDay != null;
  const { data, isLoading } = useMonthlyBalances(account.id, open, byCut ? "cut" : "month");
  const rows = data?.months ?? [];
  return (
    <div className="pt-2 border-t border-base-300/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-base-content/60 hover:text-primary inline-flex items-center gap-1"
      >
        {open ? (
          <ChevronDown className="size-3.5" />
        ) : (
          <ChevronRight className="size-3.5" />
        )}
        {byCut ? "Historial por corte" : "Historial mensual"}
      </button>
      {open && (
        <div className="mt-2">
          {isCredit && (
            <div className="text-[11px] text-base-content/45 mb-1">
              {byCut
                ? `Corta el día ${account.cutDay} · negativo = deuda, positivo = a favor`
                : "Negativo = deuda, positivo = a favor. Configura el día de corte en Editar para agrupar por estado de cuenta."}
            </div>
          )}
          {isLoading ? (
            <div className="text-xs text-base-content/55 py-2">Cargando…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-base-content/55 py-2">
              Sin movimientos registrados en esta cuenta.
            </div>
          ) : (
            <ul className="text-xs space-y-1 max-h-48 overflow-y-auto">
              {rows
                .slice()
                .reverse() /* Más reciente primero. */
                .map((r) => (
                  <li
                    key={r.period}
                    className="grid grid-cols-[auto_1fr_1fr] gap-2 items-baseline"
                    title={`Entradas ${formatMxnCents(r.inflowCents)} · Salidas ${formatMxnCents(r.outflowCents)}`}
                  >
                    <span className="text-base-content/55 font-mono">{r.period}</span>
                    <span className="text-base-content/65 tabular-nums">
                      Inicio: {formatMxnCents(r.openingCents)}
                    </span>
                    <span
                      className={clsx(
                        "tabular-nums font-medium text-right",
                        r.closingCents < 0
                          ? "text-error"
                          : isCredit && r.closingCents > 0
                            ? "text-success"
                            : "text-base-content",
                      )}
                    >
                      Final: {formatMxnCents(r.closingCents)}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function CreditCardBalance({
  account,
  balance,
}: {
  account: Account;
  balance: AccountBalance | undefined;
}) {
  const limit = account.creditLimitCents ?? 0;
  const used = balance?.usedCents ?? account.openingBalanceCents;
  const available = balance?.availableCents ?? limit - used;
  const utilizationPct = limit > 0 ? Math.max(0, Math.min(100, (used / limit) * 100)) : 0;
  const utilizationColor =
    utilizationPct < 30 ? "bg-success" : utilizationPct < 70 ? "bg-warning" : "bg-error";
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-base-content/55 font-semibold">
            Disponible
          </div>
          <div
            className={clsx(
              "text-2xl font-display font-bold tabular-nums",
              available < 0 ? "text-error" : "text-success",
            )}
          >
            {formatMxnCents(available)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-base-content/55">de {formatMxnCents(limit)}</div>
          <div className="text-[11px] text-base-content/45 tabular-nums">
            {Math.round(utilizationPct)}% usado
          </div>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-base-200 overflow-hidden">
        <div
          className={clsx("h-full transition-all", utilizationColor)}
          style={{ width: `${utilizationPct}%` }}
        />
      </div>
      <div className="text-xs text-base-content/55 flex items-center justify-between">
        <span>
          Deuda: <span className="font-mono tabular-nums">{formatMxnCents(used)}</span>
        </span>
        {account.openingBalanceCents !== 0 && (
          <span className="text-base-content/45">
            inicial: {formatMxnCents(account.openingBalanceCents)}
          </span>
        )}
      </div>
      <MonthlyHistory account={account} />
    </div>
  );
}

function LoanBalance({
  account,
  balance,
}: {
  account: Account;
  balance: AccountBalance | undefined;
}) {
  const remaining = balance?.loanRemainingCents ?? account.openingBalanceCents;
  const original =
    balance?.loanOriginalCents ?? account.loanOriginalCents ?? account.openingBalanceCents;
  const monthly = account.loanMonthlyPaymentCents ?? 0;
  const progress = balance?.loanProgressPct ?? null;
  const paidThisPeriod = balance?.loanPaidCents ?? 0;
  const nextDate = account.loanNextPaymentDate;
  const ratePct = account.loanInterestRateBps != null ? account.loanInterestRateBps / 100 : null;
  const totalPayments = account.loanTotalPayments;
  const progressClamped = Math.max(0, Math.min(100, progress ?? 0));

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-base-content/55 font-semibold">
            Saldo restante
          </div>
          <div className="text-2xl font-display font-bold tabular-nums text-error">
            {formatMxnCents(remaining)}
          </div>
        </div>
        <div className="text-right">
          {original > 0 && (
            <div className="text-xs text-base-content/55">de {formatMxnCents(original)}</div>
          )}
          {progress != null && (
            <div className="text-[11px] text-base-content/45 tabular-nums">{progress}% pagado</div>
          )}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-base-200 overflow-hidden">
        <div
          className="h-full transition-all bg-success"
          style={{ width: `${progressClamped}%` }}
        />
      </div>
      {monthly > 0 && (
        <div className="text-xs text-base-content/65 flex items-center justify-between border-t border-base-300/40 pt-2">
          <span>
            Pago mensual:{" "}
            <span className="font-mono tabular-nums font-semibold">{formatMxnCents(monthly)}</span>
          </span>
          {nextDate && <span className="text-base-content/50">Próx: {nextDate}</span>}
        </div>
      )}
      {paidThisPeriod > 0 && (
        <div className="text-[11px] text-success/80 tabular-nums">
          Pagos detectados en periodo:{" "}
          <span className="font-mono font-semibold">{formatMxnCents(paidThisPeriod)}</span>
        </div>
      )}
      {(ratePct != null || totalPayments != null) && (
        <div className="text-[11px] text-base-content/50 flex items-center gap-3 tabular-nums">
          {ratePct != null && <span>Tasa: {ratePct.toFixed(2)}%</span>}
          {totalPayments != null && <span>Plazos: {totalPayments}</span>}
          {account.loanFolio && <span>Folio: {account.loanFolio}</span>}
        </div>
      )}
    </div>
  );
}

function AccountModal({
  mode,
  account,
  onClose,
}: {
  mode: "create" | "edit";
  account?: Account;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<AccountCreateInput>({
    resolver: zodResolver(accountCreateSchema),
    defaultValues: account
      ? {
          name: account.name,
          type: account.type,
          bankName: account.bankName ?? undefined,
          creditLimitCents: account.creditLimitCents ?? undefined,
          cutDay: account.cutDay ?? undefined,
          currency: account.currency,
          openingBalanceCents: account.openingBalanceCents,
          notes: account.notes ?? undefined,
          allowedPaymentMethods: account.allowedPaymentMethods,
          defaultPaymentMethod: account.defaultPaymentMethod,
          loanOriginalCents: account.loanOriginalCents ?? undefined,
          loanMonthlyPaymentCents: account.loanMonthlyPaymentCents ?? undefined,
          loanInterestRateBps: account.loanInterestRateBps ?? undefined,
          loanTotalPayments: account.loanTotalPayments ?? undefined,
          loanPaymentDay: account.loanPaymentDay ?? undefined,
          loanFolio: account.loanFolio ?? undefined,
          loanNextPaymentDate: account.loanNextPaymentDate ?? undefined,
        }
      : {
          type: "bank",
          currency: "MXN",
          openingBalanceCents: 0,
          allowedPaymentMethods: [...PAYMENT_METHODS_BY_ACCOUNT_TYPE.bank.allowed],
          defaultPaymentMethod: PAYMENT_METHODS_BY_ACCOUNT_TYPE.bank.default,
        },
  });

  const type = watch("type");
  const allowedMethods = (watch("allowedPaymentMethods") ?? []) as PaymentMethod[];

  // En create-mode, al cambiar el tipo de cuenta, prerellenamos métodos
  // según el preset. En edit-mode preservamos la config del usuario.
  // El ref previene resetear en el mount inicial (el preset ya viene en
  // defaultValues).
  const isCreate = mode === "create";
  const lastTypeRef = useRef(type);
  useEffect(() => {
    if (!isCreate) return;
    if (lastTypeRef.current === type) return;
    lastTypeRef.current = type;
    const preset = PAYMENT_METHODS_BY_ACCOUNT_TYPE[type ?? "bank"];
    setValue("allowedPaymentMethods", [...preset.allowed]);
    setValue("defaultPaymentMethod", preset.default);
  }, [type, isCreate, setValue]);

  const save = useMutation({
    mutationFn: (data: AccountCreateInput) =>
      mode === "create"
        ? api.post("/finance/accounts", data)
        : api.patch(`/finance/accounts/${account!.id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance", "accounts"] });
      onClose();
    },
  });

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-lg">
        <h3 className="font-display font-semibold text-lg mb-4">
          {mode === "create" ? "Nueva cuenta" : "Editar cuenta"}
        </h3>
        <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-3">
          <Field label="Nombre" error={errors.name?.message} required>
            <input
              className="input input-bordered w-full"
              placeholder="Ej. BBVA principal · Konfio empresa"
              autoFocus
              {...register("name")}
            />
          </Field>

          <Field label="Tipo" required>
            <select className="select select-bordered w-full" {...register("type")}>
              {ACCOUNT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_META[t].label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Banco" help="BBVA, Inbursa, Santander, Konfio…">
            <input
              className="input input-bordered w-full"
              placeholder="BBVA"
              {...register("bankName")}
            />
          </Field>

          <Field
            label="Métodos de pago permitidos"
            required
            error={
              (errors as { allowedPaymentMethods?: { message?: string } }).allowedPaymentMethods
                ?.message
            }
            help="Solo estos aparecerán al registrar una transacción contra esta cuenta."
          >
            <Controller
              control={control}
              name="allowedPaymentMethods"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {PAYMENT_METHODS.map((m) => {
                    const value = (field.value ?? []) as PaymentMethod[];
                    const checked = value.includes(m);
                    return (
                      <label
                        key={m}
                        className={clsx(
                          "label cursor-pointer gap-2 px-3 py-1 rounded-lg border",
                          checked ? "border-primary bg-primary/10" : "border-base-300",
                        )}
                      >
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...value, m]
                              : value.filter((x) => x !== m);
                            field.onChange(next);
                            // Si el default queda fuera, lo limpiamos.
                            const currentDefault = watch("defaultPaymentMethod");
                            if (currentDefault && !next.includes(currentDefault)) {
                              setValue("defaultPaymentMethod", next[0] ?? null);
                            }
                          }}
                        />
                        <span className="text-sm">{PAYMENT_METHOD_LABELS[m]}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            />
          </Field>

          <Field
            label="Default sugerido"
            error={
              (errors as { defaultPaymentMethod?: { message?: string } }).defaultPaymentMethod
                ?.message
            }
            help={
              allowedMethods.length === 1
                ? "Con un solo método, se autollena disabled al elegir esta cuenta."
                : "El modal de transacción prellenará este método; el usuario puede cambiarlo."
            }
          >
            <Controller
              control={control}
              name="defaultPaymentMethod"
              render={({ field }) => (
                <select
                  className="select select-bordered w-full"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                >
                  <option value="">— sin default —</option>
                  {allowedMethods.map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </option>
                  ))}
                </select>
              )}
            />
          </Field>

          {type === "credit_card" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="Línea de crédito (MXN)"
                error={errors.creditLimitCents?.message}
                required
                help="Límite autorizado de la tarjeta. Se mostrará en el dashboard."
              >
                <Controller
                  control={control}
                  name="creditLimitCents"
                  render={({ field }) => (
                    <PesosInput
                      value={field.value ?? 0}
                      onChange={(cents) => field.onChange(cents)}
                      placeholder="100,000.00"
                    />
                  )}
                />
              </Field>
              <Field
                label="Día de corte"
                error={errors.cutDay?.message}
                help="Día del mes en que corta el estado de cuenta (Konfio: 17). Agrupa el historial por corte."
              >
                <input
                  type="number"
                  min={1}
                  max={31}
                  className="input input-bordered w-full"
                  placeholder="17"
                  {...register("cutDay", {
                    setValueAs: (v) => (v === "" || v == null ? null : Number(v)),
                  })}
                />
              </Field>
            </div>
          )}

          {type === "loan" && (
            <div className="space-y-3 border-l-2 border-error/30 pl-3">
              <Field
                label="Pago mensual (MXN)"
                error={errors.loanMonthlyPaymentCents?.message}
                required
                help="Cantidad fija que se cobra cada mes (domiciliación)."
              >
                <Controller
                  control={control}
                  name="loanMonthlyPaymentCents"
                  render={({ field }) => (
                    <PesosInput
                      value={field.value ?? 0}
                      onChange={(cents) => field.onChange(cents)}
                      placeholder="12,523.53"
                    />
                  )}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Monto original (MXN)" help="Capital original prestado.">
                  <Controller
                    control={control}
                    name="loanOriginalCents"
                    render={({ field }) => (
                      <PesosInput
                        value={field.value ?? 0}
                        onChange={(cents) => field.onChange(cents)}
                        placeholder="148,320.00"
                      />
                    )}
                  />
                </Field>
                <Field label="Tasa anual (%)" help="Tasa nominal anual.">
                  <Controller
                    control={control}
                    name="loanInterestRateBps"
                    render={({ field }) => (
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        max={1000}
                        className="input input-bordered w-full"
                        placeholder="58.00"
                        value={field.value != null ? (field.value / 100).toFixed(2) : ""}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          field.onChange(Number.isFinite(v) ? Math.round(v * 100) : undefined);
                        }}
                      />
                    )}
                  />
                </Field>
                <Field label="Plazos totales" help="Número total de pagos.">
                  <input
                    type="number"
                    min={1}
                    max={360}
                    className="input input-bordered w-full"
                    placeholder="18"
                    {...register("loanTotalPayments", { valueAsNumber: true })}
                  />
                </Field>
                <Field label="Día de pago" help="Día del mes (1-31).">
                  <input
                    type="number"
                    min={1}
                    max={31}
                    className="input input-bordered w-full"
                    placeholder="8"
                    {...register("loanPaymentDay", { valueAsNumber: true })}
                  />
                </Field>
                <Field label="Próximo pago" help="YYYY-MM-DD.">
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    {...register("loanNextPaymentDate")}
                  />
                </Field>
                <Field label="Folio" help="Folio del préstamo en el banco.">
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="295737"
                    {...register("loanFolio")}
                  />
                </Field>
              </div>
            </div>
          )}

          <Field
            label={type === "loan" ? "Deuda actual (MXN)" : "Saldo inicial (MXN)"}
            help={
              type === "credit_card"
                ? "Si la tarjeta ya tenía deuda al iniciar el panel, métela aquí (positiva = deuda)."
                : type === "loan"
                  ? "Saldo restante del préstamo HOY según el banco. Se respeta literal: los pagos importados aparecen para auditoría pero no descuentan más. Cuando Konfio actualice el saldo, edita este campo."
                  : "Cuánto había en la cuenta cuando empezaste a usar el panel."
            }
          >
            <Controller
              control={control}
              name="openingBalanceCents"
              render={({ field }) => (
                <PesosInput value={field.value ?? 0} onChange={field.onChange} />
              )}
            />
          </Field>

          <Field label="Notas">
            <textarea
              rows={2}
              className="textarea textarea-bordered w-full"
              placeholder="Cuenta principal de la operación, etc."
              {...register("notes")}
            />
          </Field>

          {save.isError && (
            <div className="alert alert-error text-sm">
              {save.error instanceof Error ? save.error.message : "Error al guardar"}
            </div>
          )}

          <div className="modal-action mt-4">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              Cancelar
            </button>
            <button type="submit" disabled={save.isPending} className="btn btn-primary">
              {save.isPending && <span className="loading loading-spinner loading-xs" />}
              {mode === "create" ? "Crear cuenta" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  help,
  required,
  children,
}: {
  label: string;
  error?: string;
  help?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="form-control w-full block">
      <div className="label py-1">
        <span className="label-text text-sm font-medium">
          {label}
          {required && <span className="text-error ml-0.5">*</span>}
        </span>
      </div>
      {children}
      {error && <p className="text-xs text-error mt-1.5 leading-relaxed">{error}</p>}
      {!error && help && (
        <p className="text-xs text-base-content/55 mt-1.5 leading-relaxed">{help}</p>
      )}
    </label>
  );
}

/**
 * Input de pesos MXN. Recibe/emite enteros en CENTS para evitar errores de
 * float, pero le muestra al usuario "1,500.00" con prefijo $. Permite negativos
 * (saldo a favor en TDC, sobregiro). El display siempre tiene 2 decimales —
 * así no hay confusión sobre si el número guardado son pesos o centavos.
 */
function PesosInput({
  value,
  onChange,
  placeholder = "0.00",
}: {
  value: number;
  onChange: (cents: number) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState((value / 100).toFixed(2));

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/55 text-sm pointer-events-none">
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        className="input input-bordered w-full pl-7 font-mono tabular-nums text-right"
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          const cleaned = v.replace(/,/g, "").trim();
          if (cleaned === "" || cleaned === "-") {
            onChange(0);
            return;
          }
          const num = parseFloat(cleaned);
          if (!Number.isNaN(num)) onChange(Math.round(num * 100));
        }}
        onBlur={() => {
          const cleaned = text.replace(/,/g, "").trim();
          const n = parseFloat(cleaned);
          if (Number.isNaN(n) || cleaned === "") {
            setText((value / 100).toFixed(2));
          } else {
            setText(n.toFixed(2));
          }
        }}
      />
    </div>
  );
}
